<template>
    <div class="input-class d-flex align-items-center gap-2 mb-3 px-2">
        <div class="search-wrapper position-relative">
            <InputText v-model="localSearch" :placeholder="t('search')" class="" />
            <i class="pi pi-search search-icon" />
        </div>

        <div class="d-flex align-items-center gap-1">
            <button class="btn btn-link p-0" @click="clearFilters" style="text-decoration: none;">
                <i style="color: #5d5f62;" class="pi pi-times-circle"></i>
                <strong class="clear-text">{{ t("clear") }}</strong>
            </button>
        </div>

        <span v-if="isMultiSelectVisible && type !== 'summaryEvents'" class="mx-1 gray-color">|</span>

        <span v-if="isMultiSelectVisible && type !== 'summaryEvents'" class="search-text">{{ t("filterBy") }} </span>
        <MultiSelect
            v-if="isMultiSelectVisible && type !== 'process' && type !== 'summaryEvents'"
            ref="statusSelect"
            v-model="localStatus"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('selectStatus')"
            :maxSelectedLabels="3"
            :showToggleAll="false"
            appendTo="self"
        >
            <template #header="{ value, options }">
                <div class="p-multiselect-header d-flex align-items-center justify-content-between w-100">
                    <div class="d-flex align-items-center">
                        <Checkbox :binary="true" :modelValue="isAllSelected(value, options)" @update:modelValue="(checked) => toggleAll(checked, options)" />
                        <span class="ms-2" style="color:#5d5f62; font-size:0.875rem;">{{ t("selectAll") }}</span>
                    </div>
                    <button type="button" class="p-link p-multiselect-close p-0" @click="statusSelect?.hide?.()">
                        <i class="pi pi-times" />
                    </button>
                </div>
            </template>
        </MultiSelect>
        <MultiSelect
            v-if="isMultiSelectVisible && type === 'process' "
            ref="statusSelect"
            v-model="selectedFilters"
            :options="filterOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('selectStatus')"
            :maxSelectedLabels="3"
            :showToggleAll="false"
            appendTo="self"
            optionGroupLabel="label"
            optionGroupChildren="items"
            style="width: 185px;"
        >
            <template #header="{ value, options }">
                <div class="p-multiselect-header d-flex align-items-center justify-content-between w-100">
                    <div class="d-flex align-items-center">
                        <Checkbox :binary="true" :modelValue="isAllSelected(value, options)" @update:modelValue="(checked) => toggleAll(checked, options)" />
                        <span class="ms-2" style="color:#5d5f62; font-size:0.875rem;">{{ t("selectAll") }}</span>
                    </div>
                    <button type="button" class="p-link p-multiselect-close p-0" @click="statusSelect?.hide?.()">
                        <i class="pi pi-times" />
                    </button>
                </div>
            </template>
        </MultiSelect>
        <div class="flex-grow-1"></div>
        <div style="color:#3C73AA; font-size: 14px; margin-left: auto;">
            {{ t('total') }} <span style="font-weight:700;">{{ totalRules }} {{ type === 'policy' ? t('policies1') : t('rules') }}</span>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, computed } from 'vue';
import Checkbox from 'primevue/checkbox'
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
    search: String,
    status: {
        type: Array,
        default: () => []
    },
    isMultiSelectVisible: {
        type: Boolean,
        default: true
    },
    totalRules: {
        type: Number,
        default: 0
    },
    type: {
        type: String,
        default: 'rule'
    }
});

const emit = defineEmits(['update:search', 'update:status', 'clear']);

const localSearch = ref(props.search || '');
const localStatus = ref(Array.isArray(props.status) ? props.status : []);
const statusSelect = ref(null);

watch(localSearch, (val) => emit('update:search', val));
watch(localStatus, (val) => emit('update:status', val));
const selectedFilters = ref([])

const filterOptions = computed(() => [
  {
    label: t('status'),
    items: [
      { label: t('enabled'), value: { type: 'status', value: 'enabled' } },
      { label: t('disabled'), value: { type: 'status', value: 'disabled' } }
    ]
  },
  {
    label: t('discoveryMode'),
    items: [
      { label: t('on'), value: { type: 'discovery', value: 'on' } },
      { label: t('off'), value: { type: 'discovery', value: 'off' } }
    ]
  }
])

watch(selectedFilters, (vals) => {
  if (props.type !== 'process') return;
  const mapped = (Array.isArray(vals) ? vals : [])
    .map(v => {
      if (v?.type === 'status') return v.value; // 'enabled' | 'disabled'
      if (v?.type === 'discovery') return v.value === 'on' ? 'discoveryOn' : 'discoveryOff';
      return null;
    })
    .filter(Boolean);

  localStatus.value = mapped;
});

function clearFilters() {
    localSearch.value = '';
    localStatus.value = [];
    selectedFilters.value = [];
    emit('clear');
}

const statusOptions = computed(() => [
    { label: t('enabled'), value: 'enabled' },
    { label: t('disabled'), value: 'disabled' }
]);

function flattenOptions(options) {
  if (!Array.isArray(options)) return [];
  return options.flatMap(o => {
    if (o && Array.isArray(o.items)) return o.items.map(i => i.value);
    if (o && Object.prototype.hasOwnProperty.call(o, 'value')) return [o.value];
    return [];
  });
}

const isAllSelected = (value, options) => {
    const selected = Array.isArray(value) ? value : [];
    const all = flattenOptions(options);
    return selected.length === all.length && all.length > 0;
};

const toggleAll = (checked, options) => {
    const all = flattenOptions(options);
    if (props.type === 'process') {
      selectedFilters.value = checked ? all : [];
    } else {
      localStatus.value = checked ? all : [];
    }
};
</script>

<style scoped>
.search-wrapper {
    position: relative;
}

.search-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    padding-left: 5px;
    border-left: 1px solid #D2D9E2;
}
.p-inputtext {
    padding: 0.25rem 0.25rem;
    font-size: 0.875rem;
    background: transparent;
}
::v-deep(.p-multiselect .p-multiselect-label) {
    font-size: 0.875rem;
    padding: 0.25rem 0.25rem !important;
}
.p-multiselect {
    background: transparent !important;
}
.clear-text {
    color: #5d5f62;
    font-size: 0.875rem;
    margin-left: 5px;
}

.search-text {
    color: #5d5f62;
    font-size: 0.875rem;
}
::v-deep(.p-multiselect .p-multiselect-header .p-multiselect-close) {
  color: #6c757d;
}
::v-deep(.p-multiselect-panel .p-checkbox .p-checkbox-box) {
  width: 18px !important;
  height: 18px !important;
}
::v-deep(.p-multiselect-panel .p-checkbox .p-checkbox-box .p-checkbox-icon) {
  font-size: 12px;
}
::v-deep(.p-multiselect-panel .p-multiselect-items .p-multiselect-item) {
  padding: 0.5rem 0.5rem;
}
::v-deep(.p-multiselect-panel .p-multiselect-header) {
    padding: 0.5rem 0.5rem;
}

::v-deep(.p-multiselect-panel .p-multiselect-items .p-multiselect-item-group) {
  font-size: 0.8rem;
  padding:0.75rem 0.75rem;
}

::v-deep(.p-multiselect-panel .p-multiselect-items ) {
  padding: 0;
}
</style>
